import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from '@angular/router';
import { LoggedInGuard } from '../wonder-core-ng/session-store/logged-in.guard';
import { DashboardApiService } from '@wonder-bi-front/dashboard-store/dashboard.api.service';
import { IDashboard } from '@wonder-bi-front/dashboard-store/interfaces/dashboard.interface';
import { catchError, firstValueFrom, of } from 'rxjs';
import { TenantSelectionService } from './session/tenant-selection.service';

const firstDashboardRedirect: CanActivateFn = async () => {
  const dashboardApi = inject(DashboardApiService);
  const router = inject(Router);
  const tenantSelection = inject(TenantSelectionService);

  let list: IDashboard[] = [];
  try {
    list = await firstValueFrom(
      dashboardApi.loadDashboards(tenantSelection.current()).pipe(catchError(() => of([])))
    );
  } catch {
    list = [];
  }

  if (list.length > 0) {
    const first = [...list].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))[0];
    return router.parseUrl('/dashboard/' + first.id);
  }
  return router.parseUrl('/no-dashboards');
};

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [LoggedInGuard, firstDashboardRedirect],
    loadChildren: () =>
      import('@wonder-bi-front/dashboard-landing/dashboard-landing.routes').then(
        (m) => m.dashboardLandingRoutes
      ),
  },
  {
    path: 'no-dashboards',
    loadChildren: () =>
      import('@wonder-bi-front/dashboard-landing/dashboard-landing.routes').then(
        (m) => m.dashboardLandingRoutes
      ),
  },
  {
    // Target of permissionGuard's redirect: without it the router throws NG04002 and lands on '/'.
    path: 'forbidden',
    loadComponent: () => import('./forbidden/forbidden.component').then((m) => m.ForbiddenComponent),
  },
  {
    path: 'admin/users',
    loadChildren: () => import('@wonder-bi-front/user/user.routes').then((m) => m.userRoutes),
  },
  {
    // Ahead of 'admin/roles': that one matches by prefix, so the router would enter it and never
    // reach this screen. The screen lives in wonder-bi-core because it needs the attribute
    // metadata catalogue, while the roles list that links to it lives in wonder-core-ng.
    path: 'admin/roles/:roleId/data-access',
    loadChildren: () =>
      import('@wonder-bi-front/role-data-access/role-data-access.routes').then(
        (m) => m.roleDataAccessRoutes
      ),
  },
  {
    path: 'admin/feature-flags',
    loadChildren: () =>
      import('@wonder-bi-front/feature-flags/feature-flags.routes').then((m) => m.featureFlagsRoutes),
  },
  {
    path: 'admin/roles',
    loadChildren: () =>
      import('@wonder-core-ng/roles-management/roles-management.routes').then(
        (m) => m.rolesManagementRoutes
      ),
  },
  {
    path: 'profile',
    loadChildren: () => import('./profile/profile.routes').then((m) => m.profileRoutes),
  },
  {
    path: 'session',
    loadChildren: () => import('./session/session.routes').then((m) => m.sessionRoutes),
  },
  {
    path: 'companies',
    loadChildren: () => import('./company/company.routes').then((m) => m.companyRoutes),
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./dashboard/dashboard.routes').then((m) => m.dashboardRoutes),
  },
  {
    path: 'query-config',
    loadChildren: () =>
      import('@wonder-bi-front/query-config-form/query-engine.routes').then(
        (m) => m.queryConfigFormRoutes
      ),
  },
  {
    path: 'chart-config',
    loadChildren: () =>
      import('@wonder-bi-front/chart-config-form/chart-config.routes').then(
        (m) => m.chartConfigRoutes
      ),
  },
  {
    path: 'dashboard-config',
    loadChildren: () =>
      import('@wonder-bi-front/dashboard-config/dashboard-config.routes').then(
        (m) => m.dashboardConfigRoutes
      ),
  },
  {
    path: 'conditions',
    loadChildren: () =>
      import('@wonder-bi-front/conditions/conditions.routes').then((m) => m.conditionsRoutes),
  },
  {
    path: 'events-config',
    loadChildren: () =>
      import('@wonder-bi-front/events-config/events-config.routes').then(
        (m) => m.eventsConfigRoutes
      ),
  },
  {
    path: 'action-lists',
    loadChildren: () =>
      import('@wonder-bi-front/action-list-config/action-list-config.routes').then(
        (m) => m.actionListConfigRoutes
      ),
  },
  {
    path: 'notifications',
    loadChildren: () =>
      import('@wonder-bi-front/notifications/notifications.routes').then(
        (m) => m.notificationsRoutes
      ),
  },
  {
    path: 'attribute-metadata',
    loadChildren: () =>
      import('@wonder-bi-front/attribute-metadata/attribute-metadata.routes').then(
        (m) => m.attributeMetadataRoutes
      ),
  },
  {
    path: 'ai-metrics',
    loadChildren: () => import('@wonder-bi-front/ai-metrics/ai-metrics.routes').then((m) => m.aiMetricsRoutes),
  },
];
